import {priority} from '@priority-software/priority-proto-api/lib';
import {Form} from '../../../stores/Form/Form';
import {texts} from '../components/AdvancedSearch/AdvancedSearchDrawer/texts';
import {SavedSearch} from '../types';
import {applyFilters} from './filters';
import {desrializeSearches, findAvailableIndex, prepareSearchFilters} from './utils/filter';
import {
    updateRecentSearches,
    updateSavedSearches
} from '../components/AdvancedSearch/AdvancedSearchDrawer/components/SavedSearches/service';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import {getUuid4} from '../../../utils/getUuid4';

export const createSavedSearch = (
    name: string,
    form: Form,
    filterOp: priority.netitems.shared.LogicalOperator = priority.netitems.shared.LogicalOperator
        .And
): SavedSearch => ({
    name,
    isDefault: false,
    filters: form.filters,
    ignoreCase: false,
    isSaved: false,
    isConverted: false,
    filterOp,
    index: findAvailableIndex(form.searchStore.savedSearches),
    id: getUuid4()
});

export const updateSearches = async (searches: SavedSearch[], formName: string) => {
    const rawSearches = desrializeSearches(searches);
    updateSavedSearches(rawSearches, formName);
};

export const updateRecentSearch = async (searches: SavedSearch[], formName: string) => {
    const rawSearches = desrializeSearches(searches);
    await updateRecentSearches(rawSearches, formName);
};

export const executeSearch = async (form: Form, search: SavedSearch, top?: number) => {
    let searchFiltersColumnId;
    const hasColumnId = search.filters[0]?.Condition[0]?.ColumnID;
    const sortData = search.filters.reduce((acc, filter) => {
        if (!!filter.sort) {
            acc.push(filter.sort);
        }
        return acc;
    }, []);

    if (!hasColumnId) {
        searchFiltersColumnId = search.filters.map(filter => {
            const column = form.getColumnByName(filter.Condition[0].ColumnName);
            const conditions = filter.Condition.map(condition => ({
                ...condition,
                ColumnID: column.ID
            }));

            return {...filter, Condition: conditions};
        });
    }

    if (!!sortData.length) {
        form.updateSort(sortData);
    }

    const filers = searchFiltersColumnId || search.filters;
    const updatedFilters = prepareSearchFilters(filers);

    form.updateFilters(updatedFilters);
    form.searchStore.setCurrentSearchId(search.id);

    await applyFilters({form, top, filtersOperator: search.filterOp});
};

export const createNewSearch = (form: Form) => {
    let savedSearch = form.searchStore.savedSearches.find(search => !search.isSaved);
    if (savedSearch) {
        savedSearch.filters = form.filters;
    } else {
        savedSearch = createSavedSearch(texts.newSearch, form);

        form.updateNewSearch(savedSearch);
    }
};

export const createRecentSearch = (form: Form) => {
    const newRecentSearch = createSavedSearch(`${texts.recentSearchName}1`, form);

    const recentSearches = form.searchStore.recentSearches.map((search, idx) => ({
        ...search,
        name: `${texts.recentSearchName}${idx + 2}`
    }));

    if (recentSearches.unshift(newRecentSearch) > 5) {
        recentSearches.pop();
    }
    form.searchStore.setRecentSearches(recentSearches);
};

export const saveRecentSearches = async (form: Form) => {
    createRecentSearch(form);
    await updateRecentSearch(form.searchStore.recentSearches, form.ename);
};

export const saveAndSearchAction = async (search: SavedSearch, form: Form) => {
    await saveAction(search, form);
    await searchAction(search, form);
};

export const saveAction = async (search: SavedSearch, form: Form) => {
    const searches = form.searchStore.savedSearches;
    const searchIndex = searches.findIndex(item => item.index === search.index);
    const isNewSearch = searchIndex === -1;

    if (!isNewSearch) {
        const newSearches = searches;
        newSearches[searchIndex] = search;
        form.searchStore.setFormSavedSearches(newSearches);
    } else {
        search.isSaved = true;
        form.updateNewSearch(search);
    }

    // get the updated savedSearches array directly from the store
    await updateSearches(form.searchStore.savedSearches, form.ename);

    if (isNewSearch) {
        const toastMessageTmpl =
            search.index <= 9
                ? texts.savedSearchSuccessMessage
                : texts.savedSearchSuccessMessageEmptyShortcut;

        showSuccessToast(
            toastMessageTmpl
                .replace('[X]', search.name)
                .replace('Ctrl+X', `Ctrl+${search.index.toString()}`)
        );
    }
};

export const searchAction = async (search: SavedSearch, form: Form) => {
    const sortData = search.filters.reduce((acc, filter) => {
        if (!!filter.sort) {
            acc.push(filter.sort);
        }
        return acc;
    }, []);

    form.updateSort(sortData);
    form.updateFilters(search.filters);
    form.searchStore.setCurrentSearchId(search.id);

    await applyFilters({form, filtersOperator: search.filterOp});
};
